/**
 * The Fields page's body — the workspace's field catalogue, grouped Profile /
 * Your columns / Derived / Activity, with what each field holds and how much
 * of the workspace has it.
 *
 * ## WHAT THIS PAGE IS FOR
 *
 * A rule can only be as good as the field it reads, and this is where a person
 * finds out what their fields really are before writing one: that "DOB" came
 * in as text because one upload wrote "n/a" in it; that "Joining date" is
 * ambiguous (03/04/2026) and needs a day-first or month-first answer before any
 * rule can use it; that "City" is known for only 62% of people, so a rule on
 * it leaves 38% out whatever it says. And it is where they say so: rename a
 * field, correct its type, answer its date format, hide it from the picker,
 * or derive a new one from it (Age from whichever column holds a birth date).
 *
 * ## EVERY COLUMN, NOT A LIST
 *
 * "Your columns" is every column of every ready upload, by its name verbatim.
 * Nothing on this page knows any column's name in advance; the owner's brief
 * ("it should not be fixed") is a property of the API, and this page just
 * draws whatever the catalogue holds — including a group that is empty, which
 * says how it gets filled rather than vanishing.
 *
 * ## HONEST FIGURES
 *
 * Coverage is `CoverageMeter` over `coverage` (0–1), and `null` — a workspace
 * with no people — is "not measured", never 0%. Samples arrive masked for
 * email and phone columns, and are shown as the API sends them. A type the
 * uploads disagree about says so in words, beside the type that won.
 *
 * Nothing here fetches: `FieldsPage` owns the reads and writes and hands them
 * in as {@link FieldActions}, so the verify suite renders this from a fixture.
 */
import {
  DERIVED_FIELD_KIND_LABEL,
  FIELD_DATE_FORMAT_LABEL,
  FIELD_DATE_FORMAT_VALUES,
  FIELD_GROUP_LABEL,
  FIELD_GROUP_VALUES,
  LATEST_UPLOAD_WINS,
  STORED_FIELD_TYPE_VALUES,
  type CreateDerivedFieldRequest,
  type DerivedFieldSuggestion,
  type Field,
  type FieldCatalogueResponse,
  type FieldDateFormat,
  type FieldGroup,
  type StoredFieldType,
  type UpdateFieldSettingsRequest,
} from '@wend/shared';
import {
  Badge,
  Button,
  Callout,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Checkbox,
  CoverageMeter,
  FormField,
  Input,
  Tag,
  buttonVariants,
  cn,
} from '@wend/ui';
import { Pencil, Plus, Sparkles, Trash2 } from 'lucide-react';
import { useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { FIELD_GROUP_TAG, FIELD_GROUP_TAG_TONE, FIELD_TYPE_LABEL, RULE_SELECT_CLASS, coveragePercent } from '../../../components/rules';
import { errorMessage } from '../../../lib/api/errors';
import { DerivedFieldDialog, ageSuggestions } from './DerivedFieldDialog';

const EN_IN = new Intl.NumberFormat('en-IN');

export interface FieldActions {
  readonly saveSettings: (key: string, request: UpdateFieldSettingsRequest) => Promise<void>;
  readonly createDerived: (request: CreateDerivedFieldRequest) => Promise<void>;
  readonly deleteDerived: (derivedFieldId: string) => Promise<void>;
}

/** What each group is, in one sentence under its heading. */
export const FIELD_GROUP_SENTENCE: Readonly<Record<FieldGroup, string>> = {
  profile: 'What Wend knows about every person, whatever their uploads held.',
  columns: 'Every column of every upload, by the name it had in the file.',
  derived: 'Worked out by Wend from another field — an age from a birth date, a band from a number.',
  activity: 'From what Wend itself sent and saw: messages, replies, clicks and outcomes.',
};

/* -------------------------------------------------------------------------- */
/* One field                                                                   */
/* -------------------------------------------------------------------------- */

function originLine(field: Field, fields: readonly Field[]): string {
  switch (field.origin.kind) {
    case 'column':
      return `Column “${field.origin.column}” · in ${field.origin.datasetCount === 1 ? '1 upload' : `${EN_IN.format(field.origin.datasetCount)} uploads`}`;
    case 'derived': {
      const sourceField = field.origin.sourceField;
      const source = fields.find((candidate) => candidate.key === sourceField)?.label ?? sourceField;
      return `${DERIVED_FIELD_KIND_LABEL[field.origin.derivedKind].split(' (')[0] ?? ''} · from ${source}`;
    }
    case 'activity':
      return 'Measured by Wend';
    default:
      return 'Built in';
  }
}

function SettingsEditor({
  field,
  onSave,
  onCancel,
}: {
  field: Field;
  onSave: (request: UpdateFieldSettingsRequest) => Promise<void>;
  onCancel: () => void;
}) {
  const typeId = useId();
  const formatId = useId();
  const isColumn = field.origin.kind === 'column';
  const [label, setLabel] = useState(field.settings?.label ?? '');
  const [type, setType] = useState<StoredFieldType | ''>(field.settings?.type ?? '');
  const [format, setFormat] = useState<FieldDateFormat | ''>(field.settings?.dateFormat ?? '');
  const [hidden, setHidden] = useState(field.hidden);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const effectiveType = type === '' ? field.type : type;

  async function save(): Promise<void> {
    setPending(true);
    setError(null);
    try {
      await onSave({
        label: label.trim() === '' ? null : label.trim(),
        ...(isColumn ? { type: type === '' ? null : type, dateFormat: effectiveType === 'date' && format !== '' ? format : null } : {}),
        hidden,
      });
    } catch (caught) {
      setError(errorMessage(caught));
      setPending(false);
    }
  }

  return (
    <div className="mt-3 flex flex-col gap-4 rounded-sm border border-line bg-black/20 p-3.5" data-role="field-settings">
      <FormField label="Label" description={`Blank uses ${field.origin.kind === 'column' ? `the column name, “${field.origin.column}”` : 'Wend’s name for it'}.`}>
        <Input value={label} onChange={(event) => setLabel(event.target.value)} disabled={pending} autoComplete="off" />
      </FormField>

      {isColumn && (
        <div className="flex flex-col gap-1.5">
          <label htmlFor={typeId} className="text-[0.86rem] font-medium text-text">
            Type
          </label>
          <select id={typeId} className={RULE_SELECT_CLASS} value={type} disabled={pending} onChange={(event) => setType(event.target.value as StoredFieldType | '')}>
            <option value="">{field.origin.kind === 'column' ? `As detected (${field.origin.detectedType})` : 'As detected'}</option>
            {STORED_FIELD_TYPE_VALUES.map((value) => (
              <option key={value} value={value}>
                {FIELD_TYPE_LABEL[value]}
              </option>
            ))}
          </select>
        </div>
      )}

      {isColumn && effectiveType === 'date' && (
        <div className="flex flex-col gap-1.5">
          <label htmlFor={formatId} className="text-[0.86rem] font-medium text-text">
            How the dates are written
          </label>
          <select id={formatId} className={RULE_SELECT_CLASS} value={format} disabled={pending} onChange={(event) => setFormat(event.target.value as FieldDateFormat | '')}>
            <option value="">Not chosen</option>
            {FIELD_DATE_FORMAT_VALUES.map((value) => (
              <option key={value} value={value}>
                {FIELD_DATE_FORMAT_LABEL[value]}
              </option>
            ))}
          </select>
        </div>
      )}

      <Checkbox
        checked={hidden}
        onCheckedChange={setHidden}
        disabled={pending}
        label="Hide from the field picker. Rules that already use it will report it as missing."
      />

      {error !== null && (
        <p role="alert" className="text-[0.84rem] text-bad">
          {error}
        </p>
      )}

      <div className="flex flex-wrap gap-2.5">
        <Button type="button" size="sm" loading={pending} onClick={() => void save()}>
          {pending ? 'Saving' : 'Save'}
        </Button>
        <Button type="button" size="sm" variant="secondary" onClick={onCancel} disabled={pending}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

function DateFormatQuestion({ field, onAnswer }: { field: Field; onAnswer: (format: FieldDateFormat) => Promise<void> }) {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <div className="mt-2.5 rounded-sm border border-warn/35 bg-warn/8 p-3" role="group" aria-label={`How ${field.label} is written`}>
      <p className="text-[0.82rem] text-warn-ink">
        Its dates look like 03/04/2026, which is two different days depending on who wrote it. Which way round are they?
        Rules cannot use it until you say — Wend never guesses.
      </p>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {FIELD_DATE_FORMAT_VALUES.map((format) => (
          <Button
            key={format}
            type="button"
            size="sm"
            variant="secondary"
            disabled={pending}
            onClick={() => {
              setPending(true);
              setError(null);
              onAnswer(format).catch((caught: unknown) => {
                setError(errorMessage(caught));
                setPending(false);
              });
            }}
          >
            {FIELD_DATE_FORMAT_LABEL[format]}
          </Button>
        ))}
      </div>
      {error !== null && (
        <p role="alert" className="mt-1.5 text-[0.8rem] text-bad">
          {error}
        </p>
      )}
    </div>
  );
}

function FieldRow({ field, fields, actions }: { field: Field; fields: readonly Field[]; actions: FieldActions }) {
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const derivedId = field.origin.kind === 'derived' ? field.origin.derivedFieldId : null;

  return (
    <li className="border-t border-line py-3.5 first:border-t-0" data-field-key={field.key}>
      <div className="grid gap-x-4 gap-y-2 sm:grid-cols-[minmax(0,1.4fr)_5.5rem_7.5rem_minmax(0,1.2fr)_auto] sm:items-center">
        <div className="min-w-0">
          <p className="flex flex-wrap items-center gap-2">
            <Tag tone={FIELD_GROUP_TAG_TONE[field.group]}>{FIELD_GROUP_TAG[field.group]}</Tag>
            <span className="font-medium text-text">{field.label}</span>
            {field.hidden && <Badge tone="neutral">Hidden</Badge>}
            {field.needsDateFormat && <Badge tone="warn">Needs a date format</Badge>}
            {field.conflict && <Badge tone="warn">Uploads disagree</Badge>}
          </p>
          <p className="mt-0.5 truncate font-mono text-[0.7rem] text-muted">{originLine(field, fields)}</p>
        </div>

        <span className="text-[0.82rem] text-text">
          <span className="sm:hidden text-muted">Type: </span>
          {FIELD_TYPE_LABEL[field.type]}
          {field.type === 'date' && field.dateFormat !== null && (
            <span className="block font-mono text-[0.66rem] text-muted">{field.dateFormat}</span>
          )}
        </span>

        <span>
          <CoverageMeter percent={coveragePercent(field)} basis="known" />
        </span>

        <span className="min-w-0 text-[0.8rem] text-muted">
          {field.samples.length === 0 ? (
            <span className="italic">No examples</span>
          ) : (
            <span className="break-words">
              <span className="sr-only">Examples: </span>
              {field.samples.join(' · ')}
            </span>
          )}
        </span>

        <span className="flex flex-wrap items-center gap-1.5 sm:justify-end">
          <Button type="button" size="sm" variant="secondary" onClick={() => setEditing((value) => !value)} aria-expanded={editing}>
            <Pencil aria-hidden="true" className="size-4" />
            <span>
              Edit<span className="sr-only"> {field.label}</span>
            </span>
          </Button>
          {derivedId !== null && (
            <Button
              type="button"
              size="sm"
              variant="secondary"
              loading={deleting}
              className="border-bad/45 text-bad hover:border-bad/70 hover:bg-bad/10"
              onClick={() => {
                setDeleting(true);
                setDeleteError(null);
                actions.deleteDerived(derivedId).catch((caught: unknown) => {
                  setDeleteError(errorMessage(caught));
                  setDeleting(false);
                });
              }}
            >
              <Trash2 aria-hidden="true" className="size-4" />
              <span>
                Delete<span className="sr-only"> {field.label}</span>
              </span>
            </Button>
          )}
        </span>
      </div>

      {field.conflict && field.origin.kind === 'column' && (
        <p className="mt-1.5 text-[0.78rem] text-warn">
          Your uploads disagree about this column’s type. The latest upload’s ({field.origin.detectedType}) is used
          {field.settings?.type != null ? `, and you set it to ${FIELD_TYPE_LABEL[field.settings.type]}` : ''}.
        </p>
      )}

      {field.needsDateFormat && !editing && (
        <DateFormatQuestion field={field} onAnswer={(format) => actions.saveSettings(field.key, { dateFormat: format })} />
      )}

      {deleteError !== null && (
        <p role="alert" className="mt-1.5 text-[0.82rem] text-bad">
          {deleteError}
        </p>
      )}

      {editing && (
        <SettingsEditor
          field={field}
          onCancel={() => setEditing(false)}
          onSave={async (request) => {
            await actions.saveSettings(field.key, request);
            setEditing(false);
          }}
        />
      )}
    </li>
  );
}

/* -------------------------------------------------------------------------- */
/* The page body                                                               */
/* -------------------------------------------------------------------------- */

function EmptyGroup({ group }: { group: FieldGroup }) {
  switch (group) {
    case 'columns':
      return (
        <p className="text-[0.88rem] text-muted">
          No uploaded columns yet. Upload a list and every column in it becomes a field here.{' '}
          <Link to="/audiences/upload" className="text-brand-ink underline-offset-4 hover:underline">
            Upload a list
          </Link>
        </p>
      );
    case 'derived':
      return <p className="text-[0.88rem] text-muted">Nothing derived yet. Add one to work out an age, a band, a month or a weekday from another field.</p>;
    default:
      return <p className="text-[0.88rem] text-muted">Nothing in this group yet.</p>;
  }
}

export function FieldsContent({ catalogue, actions }: { catalogue: FieldCatalogueResponse; actions: FieldActions }) {
  const [dialog, setDialog] = useState<{ initial: DerivedFieldSuggestion | null } | null>(null);
  const openerRef = useRef<HTMLButtonElement>(null);
  const suggestions = ageSuggestions(catalogue.fields, catalogue.suggestions);

  function closeDialog(): void {
    setDialog(null);
    openerRef.current?.focus();
  }

  return (
    <div className="flex flex-col gap-6" data-role="fields">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-prose">
          <p className="text-[0.92rem] text-text" data-role="fields-summary">
            {`${EN_IN.format(catalogue.fields.length)} ${catalogue.fields.length === 1 ? 'field' : 'fields'}, `}
            {catalogue.people === 0
              ? 'and no people yet, so no coverage can be measured.'
              : `measured over the ${EN_IN.format(catalogue.people)} ${catalogue.people === 1 ? 'person' : 'people'} in this workspace.`}
          </p>
          <p className="mt-1 text-[0.82rem] text-muted">{LATEST_UPLOAD_WINS}</p>
        </div>
        <Button type="button" ref={openerRef} onClick={() => setDialog({ initial: null })}>
          <Plus aria-hidden="true" className="size-4" />
          Add derived field
        </Button>
      </div>

      {suggestions.length > 0 && (
        <Callout tone="brand" title="Suggested" icon={<Sparkles aria-hidden="true" className="size-4" />}>
          <p className="mb-2">These columns read like birth dates. One click opens the form filled in — nothing is added until you confirm.</p>
          <div className="flex flex-wrap gap-1.5">
            {suggestions.map((suggestion) => {
              const source = catalogue.fields.find((field) => field.key === suggestion.sourceField)?.label ?? suggestion.sourceField;
              return (
                <button
                  key={`${suggestion.kind}:${suggestion.sourceField}`}
                  type="button"
                  data-role="age-suggestion"
                  className={cn(buttonVariants({ variant: 'secondary', size: 'sm' }))}
                  onClick={() => setDialog({ initial: suggestion })}
                >
                  <Sparkles aria-hidden="true" className="size-4" />
                  {`${suggestion.label} from ${source}`}
                </button>
              );
            })}
          </div>
        </Callout>
      )}

      {FIELD_GROUP_VALUES.map((group) => {
        const fields = catalogue.fields.filter((field) => field.group === group);
        return (
          <Card key={group} data-group={group}>
            <CardHeader>
              <CardTitle>{FIELD_GROUP_LABEL[group]}</CardTitle>
              <CardDescription>{FIELD_GROUP_SENTENCE[group]}</CardDescription>
            </CardHeader>
            <CardContent>
              {fields.length === 0 ? (
                <EmptyGroup group={group} />
              ) : (
                <>
                  <div className="hidden grid-cols-[minmax(0,1.4fr)_5.5rem_7.5rem_minmax(0,1.2fr)_auto] gap-x-4 border-b border-line pb-2 font-mono text-[0.62rem] uppercase tracking-[0.13em] text-muted sm:grid" aria-hidden="true">
                    <span>Field</span>
                    <span>Type</span>
                    <span>Known for</span>
                    <span>Examples</span>
                    <span />
                  </div>
                  <ul aria-label={`${FIELD_GROUP_LABEL[group]} fields`}>
                    {fields.map((field) => (
                      <FieldRow key={field.key} field={field} fields={catalogue.fields} actions={actions} />
                    ))}
                  </ul>
                </>
              )}
            </CardContent>
          </Card>
        );
      })}

      {dialog !== null && (
        <DerivedFieldDialog
          fields={catalogue.fields}
          suggestions={catalogue.suggestions}
          initial={dialog.initial}
          onClose={closeDialog}
          onCreate={async (request) => {
            await actions.createDerived(request);
            closeDialog();
          }}
        />
      )}
    </div>
  );
}
